import { useEffect, useMemo, useRef, useState } from "react";
import { api, ASSET_BASE } from "@/lib/api";
import { useLocale } from "@/lib/LocaleContext";

/**
 * "Trusted By" section — premium horizontal auto-scrolling marquee that
 * shows the venues DJ SPY has performed at. Renders **only** from the
 * public `/api/trusted-by` endpoint; venues.js is used only as a build-
 * time seed on the backend.
 *
 * Reduced-motion / low-power devices see a clean static grid.
 * SSR / SEO-safe: the heading + subheading + country line render
 * synchronously (SPA hydration only removes the placeholder skeleton).
 */

const SPEED_TO_SECONDS = { slow: 60, medium: 40, fast: 24 };
const LOGO_SIZE_TO_PX = { small: 44, medium: 60, large: 80 };

function resolveLogoSrc(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${ASSET_BASE}${url}`;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(mq.matches);
    handler();
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

export default function TrustedBy() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const reduced = useReducedMotion();
  const marqueeRef = useRef(null);
  const { lang } = useLocale();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/trusted-by");
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const settings = data?.settings;
  const venues = data?.venues || [];

  // Duplicated list so the CSS marquee loops seamlessly.
  const marqueeList = useMemo(() => (venues.length ? [...venues, ...venues] : []), [venues]);

  // If public API failed OR the admin disabled the section OR there are no
  // enabled venues → do NOT render anything (fail gracefully).
  if (error) return null;
  if (data && (!settings?.enabled || venues.length === 0)) return null;

  const marqueeEnabled = !!settings?.marquee_enabled && !reduced && venues.length > 3;
  const pauseOnHover = !!settings?.pause_on_hover;
  const shineEnabled = !!settings?.shine_enabled && !reduced;
  const speedSeconds = SPEED_TO_SECONDS[settings?.marquee_speed] || 40;
  const shineInterval = Math.max(2, Math.min(60, settings?.shine_interval_seconds || 7));
  const logoHeight = LOGO_SIZE_TO_PX[settings?.logo_size] || 60;

  const heading = (lang === "ar" && settings?.heading_ar) || settings?.heading || "PERFORMED AT LEADING VENUES";
  const subheading = (lang === "ar" && settings?.subheading_ar) || settings?.subheading || "";
  const countryLine = (lang === "ar" && settings?.country_line_ar) || settings?.country_line || "";

  return (
    <section
      data-testid="trusted-by-section"
      aria-labelledby="trusted-by-heading"
      className="relative overflow-hidden bg-[#050505] border-t border-b border-white/5 py-14 md:py-20"
      style={{
        "--tb-speed": `${speedSeconds}s`,
        "--tb-shine-interval": `${shineInterval}s`,
        "--tb-logo-h": `${logoHeight}px`,
      }}
    >
      {/* Subtle vertical gradient accents */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(1200px 200px at 50% -10%, rgba(239,43,43,0.10), transparent 60%), radial-gradient(900px 160px at 50% 110%, rgba(239,43,43,0.06), transparent 60%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 mb-10 md:mb-14">
        <p className="font-mono text-[11px] tracking-[0.3em] text-red-brand" data-testid="trusted-by-eyebrow">
          {lang === "ar" ? "// \u0623\u0645\u0627\u0643\u0646 \u0627\u0644\u0639\u0631\u0648\u0636" : "// PERFORMED AT"}
        </p>
        <h2
          id="trusted-by-heading"
          data-testid="trusted-by-heading"
          className="font-anton text-4xl sm:text-5xl md:text-6xl leading-[0.95] mt-2"
        >
          {heading}
        </h2>
        {subheading && (
          <p
            data-testid="trusted-by-subheading"
            className="mt-4 text-white/70 max-w-3xl text-base md:text-lg leading-relaxed"
          >
            {subheading}
          </p>
        )}
        {countryLine && (
          <p
            data-testid="trusted-by-country-line"
            className="mt-3 font-mono text-[11px] md:text-xs tracking-[0.25em] text-white/40 uppercase"
          >
            {countryLine}
          </p>
        )}
      </div>

      {/* Marquee viewport (fade masks on left/right for premium feel).
          `dir="ltr"` is critical — without it, an RTL ancestor (`/ar/*`)
          anchors the wider-than-viewport track to the RIGHT edge of the
          viewport, which pushes the `translate3d(0)` starting position
          off-screen left, and the `-50%` keyframe pushes it even further.
          The result: logos appear once, drift off, and never return. LTR
          on the viewport keeps the track anchored to the LEFT edge so
          the standard left-scrolling marquee loops seamlessly. */}
      {data ? (
        <div
          ref={marqueeRef}
          data-testid="trusted-by-marquee"
          data-marquee-enabled={marqueeEnabled ? "1" : "0"}
          className="relative"
          dir="ltr"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
          }}
        >
          {marqueeEnabled ? (
            <div
              className={`trusted-by-track flex items-center gap-10 md:gap-14 will-change-transform ${pauseOnHover ? "trusted-by-track--pauseable" : ""}`}
              style={{ animation: `trusted-by-scroll var(--tb-speed) linear infinite` }}
            >
              {marqueeList.map((v, idx) => (
                <LogoTile
                  key={`${v.id}-${idx}`}
                  venue={v}
                  index={idx}
                  duplicate={idx >= venues.length}
                  logoHeight={logoHeight}
                  shineEnabled={shineEnabled}
                />
              ))}
            </div>
          ) : (
            // Static, reduced-motion / desktop-safe grid
            <div
              data-testid="trusted-by-static-grid"
              className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8"
            >
              {venues.map((v, idx) => (
                <LogoTile
                  key={v.id}
                  venue={v}
                  index={idx}
                  duplicate={false}
                  logoHeight={logoHeight}
                  shineEnabled={shineEnabled}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // Loading skeleton — matches height so no layout shift
        <div className="relative h-[120px] md:h-[160px]" aria-hidden="true" />
      )}
    </section>
  );
}

function LogoTile({ venue, index, duplicate, logoHeight, shineEnabled }) {
  const src = resolveLogoSrc(venue.logo_url);
  const canLink =
    !!venue.website_url && !!venue.open_website_on_click && !duplicate;
  const gray = venue.grayscale !== false;
  const shine = shineEnabled && venue.shine !== false;

  // Cycle 7 — apply admin-set logo fitting.  All fields have server-side
  // clamped defaults so an unset ``logo_fit`` renders identically to
  // pre-Cycle-7 (fit=contain, scale=1, offset 0, padding 0).
  const fit = (venue.logo_fit || {});
  const fitMode = fit.fit || "contain";
  const scale = typeof fit.scale === "number" ? fit.scale : 1;
  const offX = typeof fit.offset_x_pct === "number" ? fit.offset_x_pct : 0;
  const offY = typeof fit.offset_y_pct === "number" ? fit.offset_y_pct : 0;
  const padPct = typeof fit.bg_padding_pct === "number" ? fit.bg_padding_pct : 0;
  const padPx = Math.round((logoHeight * padPct) / 100);
  const imgStyle = {
    maxHeight: logoHeight,
    maxWidth: 160,
    objectFit: fitMode,
    transform: `translate(${offX}%, ${offY}%) scale(${scale})`,
    transformOrigin: "center center",
    padding: padPx,
    boxSizing: "border-box",
  };

  const inner = (
    <div
      className="trusted-by-logo-frame relative flex items-center justify-center px-4 md:px-6"
      style={{ minWidth: 140, height: logoHeight + 24 }}
    >
      {shine && (
        <span
          aria-hidden="true"
          className="trusted-by-shine"
          style={{ animationDelay: `${(index * 0.75) % 6}s` }}
        />
      )}
      <img
        src={src}
        alt={duplicate ? "" : venue.alt_text || `${venue.name} logo`}
        loading="lazy"
        aria-hidden={duplicate ? "true" : undefined}
        className={`trusted-by-logo ${gray ? "trusted-by-logo--gray" : ""}`}
        style={imgStyle}
        onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
      />
    </div>
  );

  if (canLink) {
    return (
      <a
        href={venue.website_url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        aria-label={`Visit ${venue.name}`}
        data-testid={`trusted-by-venue-${venue.id}`}
        className="trusted-by-tile focus:outline-none focus:ring-2 focus:ring-red-brand/60 rounded-md"
      >
        {inner}
      </a>
    );
  }
  return (
    <div
      className="trusted-by-tile"
      data-testid={duplicate ? undefined : `trusted-by-venue-${venue.id}`}
      aria-hidden={duplicate ? "true" : undefined}
    >
      {inner}
    </div>
  );
}
